'use client'

import Link from '@/lib/react-compat/link'
import Image from '@/lib/react-compat/image'
import { useEffect, useState, type MouseEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Menu, X, LogIn, LogOut, User } from 'lucide-react'
import { NotificationCenter } from '@/components/notification-center'
import { useAuth } from '@/components/auth-context'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useRouter } from '@/lib/react-compat/navigation'
import { LanguageSwitcher, useI18n } from '@/components/i18n-provider'
import { useToast } from '@/hooks/use-toast'
import { Skeleton } from '@/components/ui/skeleton'
import { ThemeToggle } from '@/components/theme-toggle'

export function Header() {
  const { t } = useI18n()
  const { toast } = useToast()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { isAuthenticated, user, logout, loading } = useAuth()
  const router = useRouter()
  const protectedRoutes = new Set([
    '/recommendation',
    '/yield-prediction',
    '/disease-detection',
    '/price-prediction',
    '/calendar',
  ])

  const navigation = [
    { name: t('header.nav.home'), href: '/' },
    { name: t('header.nav.recommendation'), href: '/recommendation' },
    { name: t('header.nav.yield'), href: '/yield-prediction' },
    { name: t('header.nav.disease'), href: '/disease-detection' },
    { name: t('header.nav.price'), href: '/price-prediction' },
    { name: t('header.nav.calendar'), href: '/calendar' },
  ]

  const handleProtectedNavClick = (e: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (loading) return
    if (isAuthenticated) return
    if (!protectedRoutes.has(href)) return

    e.preventDefault()
    setIsMenuOpen(false)
    toast({
      title: '请先登录',
      description: '该功能需要登录后才能使用',
      variant: 'destructive',
    })
    router.push(`/login?returnUrl=${encodeURIComponent(href)}`)
  }

  // 开发环境下提前预取重型页面，平摊首编译时间，提升首次点击速度
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return
    const pagesToPrefetch = [
      '/price-prediction',
      '/yield-prediction',
      '/recommendation',
      '/calendar',
      '/disease-detection',
      // 仪表盘常用页（仅登录时预取，避免 401）
      ...(isAuthenticated
        ? [
            '/dashboard',
            '/dashboard/price-prediction',
            '/dashboard/yield-prediction',
            '/dashboard/recommendation',
            '/dashboard/notification-center',
          ]
        : []),
    ]
    const idle = (cb: () => void, delay = 0) => {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        return (window as any).requestIdleCallback(cb, { timeout: 1500 })
      }
      return setTimeout(cb, delay)
    }

    const timers: Array<ReturnType<typeof setTimeout> | number> = []
    pagesToPrefetch.forEach((href, idx) => {
      const handler = () => {
        try {
          router.prefetch(href)
        } catch {
          // ignore prefetch errors in dev
        }
      }
      timers.push(idle(handler, 200 * idx))
    })
    return () => {
      timers.forEach((t) => {
        if (typeof t === 'number') {
          clearTimeout(t)
        } else {
          clearTimeout(t as any)
        }
      })
    }
  }, [router, isAuthenticated])

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/50 shadow-[0_8px_24px_-12px_rgba(16,24,40,0.2)] relative">
      <div className="container flex h-16 items-center justify-between">
        <Link href="/" className="flex items-center space-x-2">
          <div className="relative h-8 w-8">
            <Image
              src="/9a42b836-c47e-40b2-a43b-b7a57f93ae1f.png"
              alt={t('brand')}
              fill
              className="rounded-sm"
            />
          </div>
          <span className="font-bold text-xl text-green-700">{t('brand')}</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6">
          {navigation.map((item) => (
            <Link
              key={item.name}
              href={item.href}
              className="text-sm font-medium transition-colors hover:text-green-600"
              onClick={(e: MouseEvent<HTMLAnchorElement>) => handleProtectedNavClick(e, item.href)}
            >
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Theme toggle */}
          <ThemeToggle />
          {/* Language switcher */}
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          {/* Notification Center - 只对已登录用户显示 */}
          {isAuthenticated && <NotificationCenter />}

          {/* User Authentication */}
          {loading ? (
            <Skeleton className="size-8 rounded-full" />
          ) : isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span className="hidden md:inline">{user?.username}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem disabled className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.username}</span>
                    <span className="text-xs text-muted-foreground">{t('header.auth.admin')}</span>
                  </div>
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => router.push('/dashboard')}
                  className="flex items-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  {t('header.auth.systemMonitor')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-red-600">
                  <LogOut className="h-4 w-4" />
                  {t('header.auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => router.push('/login')}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <LogIn className="h-4 w-4 mr-2" />
              {t('header.auth.login')}
            </Button>
          )}

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={`md:hidden border-t bg-background transition-all duration-300 ease-out overflow-hidden ${
          isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="container py-4 space-y-2">
          {navigation.map((item, index) => (
            <Link
              key={item.name}
              href={item.href}
              className={`block py-2 text-sm font-medium transition-all duration-300 ease-out hover:text-green-600 hover:translate-x-2 ${
                isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}
              style={{
                transitionDelay: isMenuOpen ? `${index * 50}ms` : '0ms',
              }}
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                handleProtectedNavClick(e, item.href)
                if (!e.defaultPrevented) {
                  setIsMenuOpen(false)
                }
              }}
            >
              {item.name}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  )
}
